import { useRef, useState, useEffect } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getInitials } from '../utils/initials';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * UserAvatar — Iter-77 · one avatar to rule them all.
 *
 * Shows the user's profile picture if uploaded, otherwise the
 * initials of their first + last name inside a Noble Empire gold ring.
 *
 * Sizes (px):
 *   xs  → 24  · list rows
 *   sm  → 32  · list rows (previous default)
 *   md  → 44  · dashboard headers (NEW default — bigger + more visible)
 *   lg  → 56  · profile pages
 *   xl  → 80  · settings / hero
 */
const SIZE_MAP = {
  xs: { box: 'w-6 h-6',   text: 'text-[10px]' },
  sm: { box: 'w-8 h-8',   text: 'text-xs' },
  md: { box: 'w-11 h-11', text: 'text-sm' },
  lg: { box: 'w-14 h-14', text: 'text-base' },
  xl: { box: 'w-20 h-20', text: 'text-xl' },
};

export const UserAvatar = ({
  name,
  email,
  photo,
  size = 'md',
  ring = true,
  className = '',
  testid,
  onClick,
  allowUpload = false,   // Iter-77 (part 5) · when true, show a camera
                         // overlay that opens a hidden file input and
                         // posts the base64 image to /api/auth/me/profile-photo
  onUploaded,            // (newPhotoDataUrl) => void
}) => {
  const cfg = SIZE_MAP[size] || SIZE_MAP.md;
  const initials = getInitials(name || email || '');
  const [src, setSrc] = useState(photo || null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Keep local src in sync when the parent hands us a new photo prop
  // (e.g. after the dashboard refetches the user) and when ANY other
  // avatar on the page uploads a new photo for the same user.
  useEffect(() => { setSrc(photo || null); }, [photo]);
  useEffect(() => {
    const onSync = (e) => {
      const next = e?.detail?.photo ?? null;
      setSrc(next);
    };
    window.addEventListener('profile-photo-updated', onSync);
    return () => window.removeEventListener('profile-photo-updated', onSync);
  }, []);

  const ringCls = ring
    ? 'ring-2 ring-[#C9A962] ring-offset-2 ring-offset-zinc-950 shadow-lg shadow-[#C9A962]/20'
    : '';
  const clickCls = onClick ? 'cursor-pointer hover:brightness-110 transition' : '';

  const openPicker = (e) => {
    e.stopPropagation();
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('Photo must be under 4MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await fetch(`${BACKEND_URL}/api/auth/me/profile-photo`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_photo: dataUrl }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Upload failed');
        return;
      }
      setSrc(dataUrl);
      // Broadcast so every dashboard (topbar chip, PM/Tenant profile
      // button, sidebar chip, welcome header) refreshes its avatar in
      // real time without a page reload.
      try {
        window.dispatchEvent(new CustomEvent('profile-photo-updated', { detail: { photo: dataUrl } }));
      } catch (_) { /* no-op */ }
      onUploaded?.(dataUrl);
      toast.success('Profile photo updated');
    } catch (_) {
      toast.error('Upload error');
    } finally { setUploading(false); }
  };

  return (
    <div
      onClick={onClick}
      className={`${cfg.box} ${ringCls} ${clickCls} relative rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900 ${className}`}
      data-testid={testid || 'user-avatar'}
      data-has-photo={src ? 'true' : 'false'}
      title={name || email || 'User'}
    >
      {src ? (
        <img
          src={src}
          alt={name || 'user'}
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <span
          className={`font-black tracking-wider ${cfg.text} bg-gradient-to-br from-[#C9A962] to-[#8B7A3F] bg-clip-text text-transparent`}
          data-testid={testid ? `${testid}-initials` : 'user-avatar-initials'}
        >
          {initials}
        </span>
      )}

      {allowUpload && (
        <>
          <button
            type="button"
            onClick={openPicker}
            disabled={uploading}
            className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 focus:opacity-100 transition bg-black/55 text-white"
            data-testid={testid ? `${testid}-camera-overlay` : 'user-avatar-camera-overlay'}
            title="Change profile photo"
            aria-label="Change profile photo"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={onFile}
            data-testid={testid ? `${testid}-camera-input` : 'user-avatar-camera-input'}
          />
        </>
      )}
    </div>
  );
};
